import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { betTypeLabel, inputMethodLabel, money, money2, pushToast, useAsync } from "../lib";
import { Empty, Skeleton, FlagBadge, Sheet, ResultChips, LoginGate } from "../ui";

const STATUS = {
  CONFIRMED: { t: "รอผล", c: "pending" },
  SETTLED: { t: "เคลียร์แล้ว", c: "lose" },
  WON: { t: "ถูกรางวัล", c: "win" },
  LOST: { t: "ไม่ถูกรางวัล", c: "lost" },
  CANCELLED: { t: "ยกเลิก", c: "lose" },
  REFUNDED: { t: "คืนเงิน", c: "lose" },
};

const PAGE_SIZE = 10;

function fmtDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
}

function BetCard({ b, busy, onCancel, onBetAgain, onViewDetail }) {
  const st = STATUS[b.status] || { t: b.status, c: "lose" };
  const canCancel = b.status === "CONFIRMED" && b.round_status === "OPEN";
  const hasPayout = Number(b.payout_total) > 0;
  const itemCount = (b.items || []).length;
  const methods = [...new Set((b.items || []).map((it) => inputMethodLabel(it.input_method)).filter(Boolean))];

  const shown = (b.items || []).slice(0, 8);
  return (
    <div className={`vx-bill st-${st.c}`}>
      <div className="vx-bill-top">
        <FlagBadge code={b.type_code} category={b.category} size={46} />
        <div className="vx-bill-id">
          <b>{b.type_name}</b>
          <span>{b.round_label}</span>
        </div>
        <span className={`lx-tag ${st.c}`}>{st.t}</span>
      </div>

      <div className="vx-bill-body">
        {!!shown.length && (
          <div className="vx-bill-nums">
            {shown.map((it) => (
              <span key={it.id} className={`vx-bill-num${Number(it.is_win) === 1 ? " won" : ""}`}>
                {it.number}
                <i>{betTypeLabel(it.bet_type_code)}</i>
              </span>
            ))}
            {itemCount > shown.length && <span className="vx-bill-more">+{itemCount - shown.length}</span>}
          </div>
        )}

        <div className="vx-bill-sums">
          <div>
            <small>ยอดแทงรวม</small>
            <b className="lx-mono">{money2(b.total_amount)}</b>
          </div>
          <div className={hasPayout ? "win" : ""}>
            <small>ผลตอบแทน</small>
            <b className="lx-mono">{hasPayout ? "+" : ""}{money2(b.payout_total)}</b>
          </div>
        </div>

        <div className="lx-bet-meta">
          <span>
            <Icon icon="solar:calendar-bold" width={11} height={11} style={{ verticalAlign: -1, marginRight: 3 }} />
            {fmtDateTime(b.created_at)}
          </span>
          <span>#{b.bet_ref}</span>
          <span>{itemCount} เลข</span>
          {methods.length > 0 && <span>แทงแบบ: {methods.join(" · ")}</span>}
          {b.note && <span>หมายเหตุ: {b.note}</span>}
        </div>

        <div className="lx-row" style={{ marginTop: 12, gap: 8 }}>
          <button className="lx-btn sm ghost" onClick={() => onViewDetail(b)}>
            <Icon icon="solar:document-text-bold" width={13} height={13} style={{ verticalAlign: -2, marginRight: 4 }} />
            ดูรายละเอียด
          </button>
          {canCancel && (
            <button className="lx-btn sm ghost" disabled={busy === b.id} onClick={() => onCancel(b.id)}>
              {busy === b.id ? "..." : "ยกเลิกโพย"}
            </button>
          )}
          <button className="lx-btn sm mint" style={{ marginLeft: "auto" }} onClick={() => onBetAgain(b)}>
            <Icon icon="solar:restart-bold" width={13} height={13} style={{ verticalAlign: -2, marginRight: 4 }} />
            แทงใหม่
          </button>
        </div>
      </div>
    </div>
  );
}

function BetDetailModal({ bet, onClose }) {
  if (!bet) return null;
  const st = STATUS[bet.status] || { t: bet.status, c: "lose" };
  return (
    <Sheet open onClose={onClose} title={`รายละเอียดบิล #${bet.bet_ref}`}>
      <div className="lx-row" style={{ gap: 10, marginBottom: 14 }}>
        <FlagBadge code={bet.type_code} category={bet.category} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{bet.type_name}</b>
          <div className="lx-muted" style={{ fontSize: 12, marginTop: 2 }}>
            {bet.round_label} · {fmtDateTime(bet.created_at)}
          </div>
        </div>
        <span className={`lx-tag ${st.c}`}>{st.t}</span>
      </div>

      {bet.result && (
        <div style={{ marginBottom: 14 }}>
          <div className="lx-muted" style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
            ผลออกรางวัล
          </div>
          <ResultChips result={bet.result} size="sm" />
        </div>
      )}

      <div className="lx-bet-stats">
        <div className="lx-bet-stat">
          <div className="lbl">รวมยอดแทง</div>
          <div className="val">{money2(bet.total_amount)}</div>
        </div>
        <div className="lx-bet-stat">
          <div className="lbl">ผลรวม</div>
          <div className={`val ${Number(bet.payout_total) > 0 ? "win" : ""}`}>{money2(bet.payout_total)}</div>
        </div>
      </div>

      <div className="lx-bet-detail-list">
        {(bet.items || []).map((it, i) => {
          const pending = it.is_win === null;
          const won = Number(it.is_win) === 1;
          return (
            <div className="lx-bet-detail-row" key={it.id}>
              <div className="lx-bet-detail-top">
                <span className="lx-bet-detail-seq">ลำดับ {i + 1}</span>
                <span className={`lx-tag ${pending ? "pending" : won ? "win" : "lost"}`}>
                  {pending ? "รอผล" : won ? "ถูกรางวัล" : "ไม่ถูกรางวัล"}
                </span>
              </div>
              <div className="lx-bet-detail-main">
                <span className="lx-bet-detail-num">{it.number}</span>
                <span className="lx-bet-detail-type">{betTypeLabel(it.bet_type_code)}</span>
                {inputMethodLabel(it.input_method) && <span className="lx-tag pending">{inputMethodLabel(it.input_method)}</span>}
              </div>
              <div className="lx-bet-detail-foot">
                <span>
                  อัตราจ่าย{it.bet_type_code === "4D_BIG" || it.bet_type_code === "4D_SMALL" ? "สูงสุด" : ""} x
                  {Number(it.payout_rate)}
                </span>
                <span>ราคาแทง {money(it.bet_amount)} บาท</span>
              </div>
              {won && <div className="lx-bet-detail-payout">ได้รับ +{money(it.payout_amount)} บาท</div>}
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}

export default function MyBetsPage({ onChange }) {
  const { isAuthenticated } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(null);
  const [detailBet, setDetailBet] = useState(null);

  const { data, loading, reload } = useAsync(
    () =>
      isAuthenticated
        ? lottoApi.myBets(
            tab === "pending"
              ? { mode: "pending", limit: 50 }
              : { mode: "history", limit: PAGE_SIZE, offset: page * PAGE_SIZE },
          )
        : Promise.resolve([]),
    [isAuthenticated, tab, page],
  );

  const { data: openRounds } = useAsync(
    () => (isAuthenticated ? lottoApi.openRounds() : Promise.resolve([])),
    [isAuthenticated],
  );

  if (!isAuthenticated)
    return (
      <LoginGate
        icon="fluent-emoji-flat:ticket"
        title="เข้าสู่ระบบเพื่อดูโพยของคุณ"
        desc="ตรวจผลทุกใบโพย ยกเลิก หรือแทงซ้ำได้ในคลิกเดียว"
        perks={["ดูผลถูก/ไม่ถูกรางวัลทันที","ยกเลิกโพยก่อนปิดรับ","แทงซ้ำเลขเดิมได้เลย"]}
        onLogin={() => nav("/auth")}
      />
    );

  const switchTab = (t) => {
    if (t === tab) return;
    setTab(t);
    setPage(0);
  };

  const cancel = async (id) => {
    setBusy(id);
    try {
      await lottoApi.cancelBet(id);
      pushToast("ยกเลิกโพยแล้ว คืนเงินเข้ากระเป๋า", "ok");
      onChange?.();
      reload();
    } catch (e) {
      pushToast(e?.response?.data?.message || "ยกเลิกไม่สำเร็จ", "err");
    } finally {
      setBusy(null);
    }
  };

  const betAgain = (b) => {
    const items = b.items || [];
    if (!items.length) return;
    const match = (openRounds || []).find((r) => r.lotto_type_id === b.lotto_type_id);
    if (!match) {
      pushToast(`ตอนนี้ยังไม่มีงวดที่เปิดอยู่สำหรับ ${b.type_name}`, "err");
      return;
    }
    const prefill = items.map((it) => ({
      betType: it.bet_type_code,
      number: it.number,
      amount: Number(it.bet_amount) || 0,
    }));
    const path = b.category === "YEEKEE" ? `/yeekee/${match.id}` : `/play/${match.id}`;
    nav(path, { state: { prefill } });
  };

  return (
    <div className="vx-bets" style={{ marginTop: 18 }}>
      <header className="lx-card vx-pagehead vx-bets-head">
        <div className="vx-bets-title">
          <span className="vx-kicker">
            <Icon icon="fluent-emoji-flat:ticket" width={16} height={16} />
            ใบโพยทั้งหมดของคุณ
          </span>
          <h1 className="lx-h1">โพยของฉัน</h1>
        </div>
        <div className="lx-tabs vx-bets-tabs">
          <button className={tab === "pending" ? "on" : ""} onClick={() => switchTab("pending")}>
            <Icon icon="solar:hourglass-bold-duotone" width={16} height={16} />
            รอผล
          </button>
          <button className={tab === "history" ? "on" : ""} onClick={() => switchTab("history")}>
            <Icon icon="solar:history-bold-duotone" width={16} height={16} />
            ประวัติเก่า
          </button>
        </div>
      </header>

      {loading && [1, 2, 3].map((i) => <Skeleton key={i} h={190} style={{ marginTop: 12 }} />)}
      {!loading && !data?.length && (
        <div className="lx-card">
          <Empty icon="solar:bill-list-bold-duotone">
            {tab === "pending" ? "ยังไม่มีโพยที่รอผล" : "ยังไม่มีประวัติการแทง"}
          </Empty>
        </div>
      )}

      <div className="vx-bill-list">
        {(data || []).map((b) => (
          <BetCard key={b.id} b={b} busy={busy} onCancel={cancel} onBetAgain={betAgain} onViewDetail={setDetailBet} />
        ))}
      </div>

      <BetDetailModal bet={detailBet} onClose={() => setDetailBet(null)} />

      {tab === "history" && !loading && (page > 0 || (data?.length || 0) >= PAGE_SIZE) && (
        <div className="lx-pager">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            <Icon icon="solar:alt-arrow-left-bold-duotone" width={14} height={14} style={{ verticalAlign: -2 }} /> ก่อนหน้า
          </button>
          <span className="pg-no">หน้า {page + 1}</span>
          <button disabled={(data?.length || 0) < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>
            ถัดไป <Icon icon="solar:alt-arrow-right-bold-duotone" width={14} height={14} style={{ verticalAlign: -2 }} />
          </button>
        </div>
      )}
    </div>
  );
}
